import React, { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { motion, AnimatePresence } from "framer-motion";
import Nav from "../components/nav";
import SEO from '../components/SEO';
import { baseUrl } from '../config';



const RotatingCube = ({ position, color }) => {
    const meshRef = useRef();

    useFrame(() => {
        meshRef.current.rotation.x += 0.01;
        meshRef.current.rotation.y += 0.01;
    });

    return (
        <mesh ref={meshRef} position={position}>
            <boxGeometry args={[2, 2, 2]} />
            <meshStandardMaterial color={color} />
        </mesh>
    );
};

const AnimatedBackground = () => {
    return (
        <Canvas
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: -1,
                width: "100%",
                height: "100%",
            }}
        >
            <ambientLight intensity={0.5} />
            <directionalLight position={[0, 10, 5]} intensity={1} />
            <OrbitControls enableZoom={false} />
            <mesh>
                <sphereGeometry args={[15, 64, 64]} />
                <meshStandardMaterial
                    color={"#00b4d8"}
                    wireframe
                    transparent
                    opacity={0.3}
                />
            </mesh>
            <RotatingCube position={[5, 5, 0]} color={"#ff6347"} />
            <RotatingCube position={[-5, -5, 0]} color={"#ffa500"} />
            <RotatingCube position={[5, -5, 0]} color={"#40e0d0"} />
        </Canvas>
    );
};

const GuidePage = () => {
    return (
        <>
            <SEO
              title="End-to-End Film Production Solution"
              description="From script to screen: ProducerBuddy's complete end-to-end film production platform covers pre-production, production, post-production, and distribution in one place."
              url="https://producerbuddy.pilomation.com/end-to-end-solution"
            />
            <Nav />
            <div className="relative h-full bg-gradient-to-br from-teal-800 via-teal-700 to-teal-500 text-black overflow-auto">
                {/* Animated Background */}
                <AnimatedBackground />

                {/* Hero Section */}
                <section className="h-screen flex flex-col justify-center items-center text-center px-6">
                    <AnimatePresence>
                        <motion.h1
                            initial={{ y: -50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 1 }}
                            className="text-6xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-cyan-500 to-teal-300 drop-shadow-lg"
                        >
                            Welcome to Your AI Film Companion
                        </motion.h1>
                    </AnimatePresence>

                    <motion.p
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.2 }}
                        className="mt-6 text-lg text-teal-900 max-w-2xl leading-relaxed"
                    >
                        Revolutionize your filmmaking process with AI-powered solutions tailored to your needs.
                    </motion.p>

                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 1 }}
                        className="flex items-center justify-center mt-8"
                    >
                        <div className="w-32 h-32 bg-gradient-to-br from-teal-300 to-cyan-400 rounded-full shadow-xl animate-pulse"></div>
                    </motion.div>

                    <button className="mt-8 px-8 py-3 bg-gradient-to-r from-teal-400 to-cyan-500 text-gray-900 font-bold rounded-full shadow-lg hover:scale-105 transition duration-300">
                        Start Exploring
                    </button>
                </section>

                {/* Solutions Section */}
                <section className="p-8">
                    <h2 className="text-4xl font-extrabold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-teal-300 to-cyan-400">
                        AI-Powered Tools for Filmmaking
                    </h2>
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-8"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 1 }}
                    >
                        {/* Solution Cards */}
                        {[
                            {
                                title: "Script Optimization",
                                description:
                                    "Enhance scripts with AI feedback on pacing, tone, and engagement.",
                            },
                            {
                                title: "Custom Chatbots",
                                description:
                                    "AI chatbots for production queries and audience engagement.",
                            },
                            {
                                title: "Workflow Automation",
                                description:
                                    "Automate scheduling, casting, and production processes.",
                            },
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="p-6 bg-gray-200 rounded-lg shadow-lg hover:scale-105 transition duration-300"
                            >
                                <h3 className="text-2xl font-bold text-teal-700 mb-2">
                                    {item.title}
                                </h3>
                                <p className="text-teal-900">{item.description}</p>
                            </div>
                        ))}
                    </motion.div>
                </section>

                {/* CTA Section */}
                <section className="py-12 text-center">
                    <motion.h2
                        initial={{ y: 50, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1 }}
                        className="text-4xl font-extrabold mb-6 text-teal-900"
                    >
                        Ready to Transform Your Film Projects?
                    </motion.h2>
                    <button className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-teal-400 text-gray-900 font-bold rounded-full shadow-lg hover:scale-105 transition duration-300">
                        Let’s Begin
                    </button>
                </section>
            </div>
        </>
    );
};

export default GuidePage;

